/**
 * 权益核销（嵌在成员详情页 `/members/:id` 的一个区块）。
 *
 * ══ 这一屏要回答的三个问题 ══
 *
 * 1. **还剩几次？** 分组余额全部来自接口（`groups[].items[]`），界面上没有任何写死的
 *    项目数、上限或天数 —— 口径一改，界面跟着变，不会出现「界面说 20 项、接口给了 21 项」。
 *
 * 2. **什么时候清零？** 倒计时用接口给的 `daysLeft`（服务端按 `valid_to` 实时算出，
 *    到期日当天为 0、已过期为负）。**前端绝不用本地时间自算天数** —— 那会让跨时区、
 *    跨年的界面上出现一个与服务端不一致的数字，而核销是否被接受由服务端裁决。
 *
 * 3. **过期和用完是不是一回事？** 不是。接口在过期时给 `remaining: null`（不是 0），
 *    因此本页对两者的呈现刻意不同：
 *      · 已过期 → 「已过期不可用」+ 算术余额注解（额度被清零规则归零）；
 *      · 已用完 → 「剩余 0 次（已用完）」（次数确实被核销掉了，可去流水里逐条追）。
 *    把过期显示成「剩余 0 次」会让操作人以为次数被谁用掉了，这是本页最不能犯的错。
 *
 * 权限：读取需要 `member:read` + 守护关系；核销动作需要 `benefit:consume` + 守护关系。
 * 全部由服务端裁决 —— 403 / 409 一律照实显示服务端原文，不做乐观更新、不伪装成成功或空列表。
 */
import { Fragment, useState } from 'react';
import { adminApi, ApiError } from '../lib/api.ts';
import type { BenefitItem } from '../lib/api.ts';
import { useLoad } from '../lib/useLoad.ts';
import { Badge, Card, Kpi, PermissionBlock } from '../components/ui.tsx';
import { EmptyBox, ErrorBox, LoadingBox, errorText } from '../components/StateBox.tsx';
import {
  MISSING_TEXT,
  benefitRemainingText,
  benefitStatusLabel,
  benefitStatusTone,
  daysLeftText,
  formatDate,
  formatDateTime,
  plainText,
  validityText,
} from '../lib/format.ts';

interface Toast {
  text: string;
  tone: 'ok' | 'error';
}

/** 核销次数的接口契约：1–100 的整数（zod 在服务端也会再判一次，这里只是提前给反馈）。 */
const MIN_COUNT = 1;
const MAX_COUNT = 100;

/**
 * 核销表单：填写次数与事由后提交。
 *
 * 失败时**原样显示服务端文案**：403 说明是权限还是守护关系，409 说明是「已过期」还是
 * 「剩余不足」——三种原因的处理方式完全不同，界面替服务端「统一成一句失败」等于把可追责的信息丢掉。
 */
function ConsumePanel({
  memberId,
  item,
  onDone,
  onCancel,
}: {
  memberId: string;
  item: BenefitItem;
  onDone: (toast: Toast) => void;
  onCancel: () => void;
}) {
  const [count, setCount] = useState('1');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (): Promise<void> => {
    const parsed = Number(count);
    if (!Number.isInteger(parsed) || parsed < MIN_COUNT || parsed > MAX_COUNT) {
      setError(`核销次数必须是 ${MIN_COUNT}–${MAX_COUNT} 的整数（接口契约；本地先拦一次，服务端仍会再判）`);
      return;
    }
    setBusy(true);
    setError('');
    try {
      const result = await adminApi.consumeBenefit(memberId, item.id, parsed, reason.trim());
      onDone({
        text:
          `已核销：《${result.name}》${result.count}${result.unit} · 事由「${result.reason}」 · ` +
          `核销后剩余 ${result.balanceAfter}${result.unit} · 有效期至 ${result.validTo} · ` +
          `登记员工工号 ${result.staffId || MISSING_TEXT}`,
        tone: 'ok',
      });
    } catch (err) {
      // 409（已过期 / 额度不足 / 并发占用）与 403（无权限 / 守护关系外）都照实显示原文：
      // 核销一旦落库，成员在自己的「数据调用记录」里就能看到，因此不可能悄悄收回。
      setError(
        err instanceof ApiError
          ? `核销未落库（${err.status}）：${err.message}`
          : '核销请求失败，请确认后端服务可用',
      );
      setBusy(false);
    }
  };

  return (
    <div className="dispatch-panel">
      <div className="dp-t">
        核销 · {item.name}
        <span className="muted text-tiny mono-num"> {item.code}</span>
      </div>
      <div className="dp-s">
        额度 {item.used} / {item.total} {item.unit}，{benefitRemainingText(item)}；有效期{' '}
        {validityText(item.validFrom, item.validTo)}（{daysLeftText(item.daysLeft)}）。
        提交后本次扣减会写入核销流水与审计链（含您的工号与事由），成员在自己的「数据调用记录」里可见；
        因此服务端拒绝时界面只显示拒绝原因，<b>不会乐观更新</b>。
      </div>
      <div className="row">
        <div className="field" style={{ maxWidth: 150 }}>
          <label className="field-label" htmlFor={`consume-count-${item.id}`}>
            核销次数（{MIN_COUNT}–{MAX_COUNT} 的整数）
          </label>
          <input
            id={`consume-count-${item.id}`}
            className="field-input"
            type="number"
            min={MIN_COUNT}
            max={MAX_COUNT}
            step={1}
            value={count}
            onChange={(e) => setCount(e.target.value)}
          />
        </div>
        <div className="field" style={{ flex: 1, minWidth: 280 }}>
          <label className="field-label" htmlFor={`consume-reason-${item.id}`}>
            核销事由（不超过 200 字，会随流水与审计一并留痕）
          </label>
          <input
            id={`consume-reason-${item.id}`}
            className="field-input"
            placeholder="例如：成员申请政策申报协助，已收到材料"
            value={reason}
            maxLength={200}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
      </div>
      {error && <div className="field-error">{error}</div>}
      <div className="row">
        <button type="button" className="btn primary sm" disabled={busy} onClick={() => void submit()}>
          {busy ? '正在核销…' : '确认核销'}
        </button>
        <button type="button" className="btn sm" disabled={busy} onClick={onCancel}>
          取消
        </button>
      </div>
    </div>
  );
}

/** 权益区块。`permissions` 来自 `/api/admin/me`（App 传入），不按角色名硬编码。 */
export function MemberBenefits({ memberId, permissions }: { memberId: string; permissions: string[] }) {
  const benefits = useLoad((signal) => adminApi.memberBenefits(memberId, signal));
  const [openId, setOpenId] = useState<string | null>(null);
  const [toast, setToast] = useState<Toast | null>(null);
  /** 核销按钮的可见性只由权限点决定；服务端才是最终裁决者。 */
  const canConsume = permissions.includes('benefit:consume');

  const handleDone = (result: Toast): void => {
    setToast(result);
    setOpenId(null);
    benefits.reload();
    window.setTimeout(() => setToast(null), 6000);
  };

  if (benefits.loading) {
    return (
      <Card title="权益核销" sub="次数型权益 · 正在向服务端读取余额">
        <LoadingBox text="正在加载权益余额与核销流水…" />
      </Card>
    );
  }

  if (benefits.error) {
    const err = benefits.error;
    // 403：可能缺 member:read，也可能是该成员不在守护关系内 —— 两种都照实说，
    // 绝不显示成「这位成员没有任何权益」（那是完全不同的结论）。
    if (err instanceof ApiError && err.status === 403) {
      return (
        <Card title="权益核销" sub="403 · 权限拦截">
          <PermissionBlock
            title="该成员的权益账目不可见"
            desc={`服务端返回：${err.message}。读取权益需要 member:read 权限点，且该成员必须在您的守护关系内；两者缺一都会被拒绝 —— 界面不会把这次拦截显示成「没有权益」或空列表。`}
          />
          <div className="sp12" />
          <button type="button" className="btn sm" onClick={benefits.reload}>
            重试
          </button>
        </Card>
      );
    }
    return (
      <Card title="权益核销" sub="读取失败">
        <ErrorBox message={errorText(err)} onRetry={benefits.reload} />
      </Card>
    );
  }

  if (!benefits.data) {
    return (
      <Card title="权益核销" sub="接口未返回数据">
        <ErrorBox message="权益数据为空" onRetry={benefits.reload} />
      </Card>
    );
  }

  const data = benefits.data;
  const summary = data.summary;
  const groups = data.groups;
  const usages = data.usages;
  // 项目数由接口下发的内容数出来，不在界面上写死「20 项」
  const itemCount = groups.reduce((total, group) => total + group.items.length, 0);

  return (
    <Card
      title="权益核销"
      sub={`${groups.length} 组 / ${itemCount} 项（分组与项目数均来自接口）`}
      actions={
        <span className="badge ghost">
          年度清零倒计时：{daysLeftText(summary.daysLeft)}
        </span>
      }
    >
      <div className="grid4">
        <Kpi
          label="年度可用剩余"
          value={summary.totalRemaining}
          tone="w"
          delta={`有效期 ${validityText(summary.validFrom, summary.validTo)}`}
        />
        <Kpi
          label="已使用"
          value={summary.totalUsed}
          delta={`年度上限合计 ${summary.totalQuota}（含过期项）`}
        />
        <Kpi
          label="已过期不可用项"
          value={summary.expiredItems}
          tone="danger"
          delta="到期即归零，与「已用完」是两件事"
        />
        <Kpi
          label="已用完项"
          value={summary.usedUpItems}
          tone="warn"
          delta="次数已核销完，可去下方流水逐条追"
        />
      </div>

      <div className="note-src">
        <b>清零规则（服务端原文）</b>
        <br />
        {plainText(data.resetNote)}
        <br />
        {plainText(data.periodNote)}
        <br />
        本年度清零还剩 {daysLeftText(summary.daysLeft)}（天数由接口按有效期算出，界面不自行计算）；
        会籍标准内包含 {summary.includedItems} 项、增值服务 {summary.valueAddedItems} 项
        （增值服务费用单独约定，不在此核销口径内）。
      </div>

      {groups.map((group) => (
        <Fragment key={group.key}>
          <div className="divider" />
          <div className="card-tt">
            <span className="bar" />
            {group.name}
            <small>
              已用 {group.usedOfGroup} / 上限 {group.totalOfGroup} · 可用 {group.remainingOfGroup} · 过期{' '}
              {group.expiredItems} 项
            </small>
          </div>
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>权益项目</th>
                  <th style={{ textAlign: 'right' }}>已用</th>
                  <th style={{ textAlign: 'right' }}>上限</th>
                  <th>剩余</th>
                  <th>单位</th>
                  <th>有效期至</th>
                  <th>清零倒计时</th>
                  <th>状态</th>
                  {canConsume && <th style={{ textAlign: 'right' }}>操作</th>}
                </tr>
              </thead>
              <tbody>
                {group.items.map((item) => (
                  <Fragment key={item.id}>
                    <tr className={openId === item.id ? 'expanded' : undefined}>
                      <td>
                        <b>{item.name}</b>
                        <div className="muted text-tiny">
                          {item.desc}
                          <span className="mono-num"> · {item.code}</span>
                        </div>
                        <div className="muted text-tiny">
                          {item.included ? '会籍标准内包含' : '增值服务（费用单独约定）'}
                          {item.perYear ? ' · 按自然年清零' : ''}
                        </div>
                      </td>
                      <td className="num-cell">{item.used}</td>
                      <td className="num-cell">{item.total}</td>
                      <td>
                        {item.expired ? (
                          <span className="badge danger">已过期不可用</span>
                        ) : (
                          <span className="mono-num">{benefitRemainingText(item)}</span>
                        )}
                        {item.expired && (
                          <div className="muted text-tiny">
                            算术余额 {item.balance}
                            {item.unit}（仅用于对账；过期是清零规则到期，不是次数被用掉）
                          </div>
                        )}
                      </td>
                      <td>{item.unit}</td>
                      <td className="mono-num">{formatDate(item.validTo)}</td>
                      <td className="mono-num">{daysLeftText(item.daysLeft)}</td>
                      <td>
                        <Badge tone={benefitStatusTone(item.status)}>{benefitStatusLabel(item.status)}</Badge>
                      </td>
                      {canConsume && (
                        <td style={{ textAlign: 'right' }}>
                          <button
                            type="button"
                            className="link"
                            onClick={() => setOpenId(openId === item.id ? null : item.id)}
                          >
                            {openId === item.id ? '收起' : '核销'}
                          </button>
                        </td>
                      )}
                    </tr>
                    {openId === item.id && (
                      <tr>
                        <td colSpan={canConsume ? 9 : 8} style={{ padding: '0 10px 14px' }}>
                          <ConsumePanel
                            memberId={memberId}
                            item={item}
                            onDone={handleDone}
                            onCancel={() => setOpenId(null)}
                          />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </Fragment>
      ))}

      {!canConsume && (
        <>
          <div className="divider" />
          <PermissionBlock
            title="当前角色没有「benefit:consume」权限点，因此本页不出现核销按钮"
            desc="核销（扣减次数并留痕）另需 benefit:consume 权限点，且该成员须在您的守护关系内；权限点来自 /api/admin/me，界面不按角色名推断。没有该权限点时权益余额仍可查看（读取只需 member:read），核销动作由服务端拒绝并原文提示。"
          />
        </>
      )}

      <div className="divider" />
      <div className="card-tt">
        <span className="bar" />
        核销流水
        <small>最近 {usages.length} 条 · 按时间倒序（服务端给出，界面不重排）</small>
      </div>
      {usages.length === 0 ? (
        <EmptyBox
          title="暂无核销流水"
          desc="这是「当前权益周期内确实没有核销记录」，不是「读不到」—— 读不到时本区块会显示服务端 403 原文。"
        />
      ) : (
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>核销时间</th>
                <th>权益项目</th>
                <th style={{ textAlign: 'right' }}>扣减</th>
                <th style={{ textAlign: 'right' }}>核销后剩余</th>
                <th>操作者</th>
                <th>事由</th>
              </tr>
            </thead>
            <tbody>
              {usages.map((usage) => (
                <tr key={usage.id}>
                  <td className="mono-num">{formatDateTime(usage.at)}</td>
                  <td>
                    <b>{usage.name}</b>
                    <span className="muted text-tiny mono-num"> · {usage.code}</span>
                  </td>
                  <td className="num-cell">{usage.count}</td>
                  <td className="num-cell">{usage.balanceAfter}</td>
                  <td>
                    {usage.staffId !== '' ? (
                      <>
                        员工代办
                        <span className="muted text-tiny mono-num"> · 工号 {usage.staffId}</span>
                      </>
                    ) : (
                      '成员自助核销'
                    )}
                  </td>
                  <td>{usage.reason !== '' ? usage.reason : MISSING_TEXT}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="note-src">
        <b>为什么「已过期」不能写成「剩余 0 次」</b>
        <br />
        接口在权益过期时把剩余次数置为 <span className="mono-num">null</span> 而不是 0：
        过期意味着清零规则到期（当年未用即归零），「剩余 0 次」意味着次数被核销消耗掉了。
        两者的处理方式完全不同，因此界面上前者显示「已过期不可用」，后者显示「剩余 0 次（已用完）」。
        倒计时天数同样来自接口的 <span className="mono-num">daysLeft</span>，界面不用本地时间自算。
      </div>

      {toast && <div className={`toast ${toast.tone}`}>{toast.text}</div>}
    </Card>
  );
}
